import React from 'react'
import { Box, IconButton, styled, useTheme } from '@mui/material'
import { Close as CloseIcon, Visibility, SaveAlt, AnalyticsOutlined } from '@mui/icons-material'
import { GridActionButton } from '../common/GridActionButton'
import { MainButton } from '../MainButton'
import { useTranslation } from 'react-i18next'
import { ReportTemplateStatus } from 'src/enums'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { IReportTemplateWarningDto } from 'src/generated-types/report-template'

export interface IReportDesignerHeaderProps {
  onPreview?: () => void
  onSaveDraft?: () => void
  onSaveReport?: () => void
  onClose?: () => void
  isEditMode?: boolean
  status?: ReportTemplateStatus
  handleChangeStatus?: (newStatus: ReportTemplateStatus) => void
  onUpgrade?: () => void
  isPreviewLoading?: boolean
  warningMessage?: string
  isUpgradeLoading?: boolean
  warnings?: IReportTemplateWarningDto
}

export const ReportDesignerHeader: React.FC<IReportDesignerHeaderProps> = ({
  onPreview,
  onSaveDraft,
  onSaveReport,
  onClose,
  isEditMode = false,
  status,
  handleChangeStatus,
  onUpgrade,
  isPreviewLoading = false,
  warningMessage = '',
  isUpgradeLoading = false,
  warnings,
}) => {
  const { t: translate } = useTranslation(['dynamic-report'])
  const theme = useTheme()
  return (
    <HeaderContainer>
      <TitleContainer>
        <AnalyticsOutlined sx={{ fontSize: '24px', color: 'primary.main' }} />
        <HeaderTitle>{translate('custom-designer-title', { ns: 'dynamic-report' })}</HeaderTitle>
      </TitleContainer>
      <ActionContainer>
        {warnings &&
          warnings?.removedInLatest.length > 0 &&
          !warnings?.isLatestVersion &&
          status !== ReportTemplateStatus.Inactive && (
            <GridActionButton
              buttonText={translate('upgrade-report', { ns: 'dynamic-report' })}
              onClick={onUpgrade}
              disabled={!isEditMode}
              textColor={getThemeColor(theme, MUIThemeColors.warningLight)}
              border={2}
              toolTipTitle={warningMessage}
              loading={isUpgradeLoading}
            />
          )}
        {status === ReportTemplateStatus.Inactive && (
          <GridActionButton
            buttonText={translate('activate', { ns: 'dynamic-report' })}
            onClick={() => handleChangeStatus?.(ReportTemplateStatus.Active)}
            disabled={!isEditMode}
            textColor={getThemeColor(theme, MUIThemeColors.successMain)}
            border={2}
          />
        )}
        <GridActionButton
          border={2}
          height="36px"
          buttonIcon={!isPreviewLoading && <Visibility sx={{ fontSize: '20px' }} />}
          buttonText={translate('preview-layout', { ns: 'dynamic-report' })}
          onClick={onPreview}
          data-cy="preview-button"
          loading={isPreviewLoading}
          disabled={status === ReportTemplateStatus.Inactive}
        />
        <GridActionButton
          border={2}
          height="36px"
          buttonIcon={<SaveAlt sx={{ fontSize: '20px' }} />}
          buttonText={translate(!isEditMode ? 'save-draft' : 'update-as-draft', { ns: 'dynamic-report' })}
          onClick={onSaveDraft}
          data-cy="save-draft-button"
          disabled={status === ReportTemplateStatus.Inactive}
        />
        <MainButton
          sx={{ minHeight: '36px' }}
          onClick={onSaveReport}
          data-cy="save-report-button"
          disabled={status === ReportTemplateStatus.Inactive}
        >
          {translate(!isEditMode ? 'save-report' : 'update-report', { ns: 'dynamic-report' })}
        </MainButton>
        <IconButton onClick={onClose} size="small" data-cy="close-button" sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </ActionContainer>
    </HeaderContainer>
  )
}

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}))

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

const ActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

const HeaderTitle = styled('h2')(({ theme }) => ({
  margin: 0,
  fontSize: '1.25rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
}))
